'use client';

import { Stack, Typography } from '@mui/material';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import Marquee from 'react-fast-marquee';
import styled from 'styled-components';
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { SwiperOptions } from 'swiper/types';

import { partners } from '@/assets/partners';
import { projects } from '@/assets/projects';
import { OwnYourWeb3IdentitySection } from '@/components/OwnYourWeb3IdentitySection';
import { useGA } from '@/hooks/useGA';

interface Project {
  id: number | string;
  name: string;
  image: string;
  href: string;
}

const StyledSwiper = styled(Swiper)`
  .swiper-wrapper {
    transition-timing-function: linear;
  }
`;

export const FeaturedProjectsSection = () => {
  const [isClient, setIsClient] = useState(false);
  const { trackButtonClickEvent } = useGA();

  useEffect(() => {
    setIsClient(true);
  }, []);

  const projectsData: Project[] = [
    {
      id: 1,
      name: 'TIKTRIX',
      image: projects.project_1,
      href: 'https://node.tiktrix.gg/',
    },
    {
      id: 2,
      name: 'VALLEY ECOSYSTEM',
      image: projects.project_2,
      href: 'https://node.valleyecosystem.com/',
    },
    {
      id: 4,
      name: 'NODE DASHBOARD',
      image: projects.project_4,
      href: 'https://lfg.nodes.magnetohr.com/',
    },
    {
      id: 5,
      name: 'NODE DASHBOARD',
      image: projects.project_5,
      href: 'https://get.flipit.com/',
    },
    {
      id: 6,
      name: 'HELIX Aegis Node',
      image: projects.project_6,
      href: 'https://node.helixgames.gg/',
    },
  ];

  // Define Swiper breakpoints with type
  const breakpoints: SwiperOptions['breakpoints'] = {
    640: {
      slidesPerView: 1,
      spaceBetween: 30,
    },
    768: {
      slidesPerView: 2,
      spaceBetween: 30,
    },
    1024: {
      slidesPerView: 2,
      spaceBetween: 30,
    },
  };

  return (
    <Stack
      width={'100%'}
      height={'100%'}
      flexGrow={1}
      paddingY={{ xs: '32px', sm: '80px' }}
      alignItems={'center'}
      component={'section'}
      position={'relative'}
      sx={{ background: 'linear-gradient(180deg, #1C2B44 0%, #060912 101.01%)' }}>
      <Stack
        bgcolor={'#060912'}
        className={'bottom-cut'}
        height={{ xs: '12px', sm: '30px' }}
        position={'absolute'}
        top={0}
        zIndex={1}
        left={'50%'}
        width={{ xs: 'min(160px, 100%)', sm: 'min(616px, 100%)' }}
        sx={{ transform: 'translateX(-50%)' }}
      />
      <Stack gap={{ xs: 4, sm: 7 }} width={'100%'} zIndex={2} padding={{ xs: '24px', sm: '80px' }}>
        <Stack gap={2} width={'100%'} textAlign={'center'}>
          <Typography
            component={'h2'}
            variant={'displayMedium'}
            className={'clip-text'}
            fontSize={{ xs: '24px', sm: '48px' }}
            lineHeight={{ xs: '32px', sm: '68px' }}
            sx={{ background: 'linear-gradient(90deg, #A9A9A9 0%, #FFFFFF 50%, #A9A9A9 100%)' }}>
            FEATURED PROJECTS
          </Typography>
          <Typography
            fontSize={{ xs: '14px', sm: '18px' }}
            component={'h4'}
            variant={'bodyLarge'}
            color={'#FFFFFF'}
            className={'mix-blend'}>
            Get inspired by successful node sales and discover new opportunities in the ecosystem.
          </Typography>
        </Stack>

        {isClient && (
          <StyledSwiper
            centeredSlides={false}
            loop={true}
            initialSlide={0}
            autoplay={{
              delay: 0,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            speed={5000}
            modules={[Autoplay]}
            className='mySwiper'
            breakpoints={breakpoints}>
            {projectsData.map(project => (
              <SwiperSlide key={project.id}>
                <a
                  href={project.href}
                  target={'_blank'}
                  rel='noreferrer'
                  onClick={() => {
                    trackButtonClickEvent({ name: 'feature_project' });
                  }}>
                  <Stack
                    width={'100%'}
                    height={{ xs: '184px', sm: '360px' }}
                    sx={{
                      backgroundImage: `url(${project.image})`,
                      backgroundSize: 'contain',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center',
                      cursor: 'pointer',
                      transition: 'transform 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.03)',
                      },
                    }}
                  />
                </a>
              </SwiperSlide>
            ))}
          </StyledSwiper>
        )}
      </Stack>
      {/* <OwnYourWeb3IdentitySection /> */}

      <Stack width={'100%'} alignItems={'center'} paddingTop={{ xs: '40px', sm: '80px' }}>
        <Stack
          gap={7}
          width={'min(1280px, 100%)'}
          zIndex={2}
          marginBottom={{ xs: '20px', sm: '40px' }}
          paddingX={{ xs: '24px', sm: 0 }}>
          <Typography
            component={'h2'}
            variant={'displayMedium'}
            className={'clip-text'}
            textAlign={'start'}
            fontSize={{ xs: '24px', sm: '48px' }}
            lineHeight={{ xs: '32px', sm: '68px' }}
            sx={{ background: 'linear-gradient(90deg, #A9A9A9 0%, #FFFFFF 50%, #A9A9A9 100%)' }}>
            TRUSTED BY
          </Typography>
        </Stack>
        <Marquee autoFill>
          {Object.entries(partners).map(([key, src]) => (
            <Stack key={key} padding={{ xs: '20px 40px', sm: '40px 80px' }}>
              <Image
                width={300}
                height={80}
                alt={key.replace(/_/g, ' ')}
                src={src}
                className={'w-full h-[40px] object-contain sm:h-[80px]'}
              />
            </Stack>
          ))}
        </Marquee>
      </Stack>
    </Stack>
  );
};
